// Login con Entra ID (Microsoft identity platform) — flujo de código + PKCE,
// SIN secretos (cliente público). Elegido expo-auth-session frente a MSAL
// nativo: misma seguridad para este caso y sin dependencias nativas propias.
//
// 🔴 EXPO GO NO PUEDE HACER ESTE LOGIN (auditado 22-08, docs/10):
// Expo Go no deja personalizar el esquema de la app, así que makeRedirectUri
// devuelve `exp://192.168.0.69:8090/--/auth`; Entra rechaza el esquema `exp://`
// «por diseño» y el proxy auth.expo.io se retiró en el SDK 48 (aviso de
// seguridad). Además la cadena dependería de la IP del Mac. Por eso:
//   · build nativa (dev build / TestFlight) → login real, redirect `onapulse://auth`
//   · Expo Go                                → VISTA PREVIA declarada (rol a mano)
// Una sola entrada en Entra («Aplicaciones móviles y de escritorio» →
// onapulse://auth) sirve para dev build, TestFlight y App Store, para siempre.
//
// El rol de la portada sale del claim `roles` del ID token (App Roles del
// registro «Ona Pulse»: DG/OPS/REV/FIN/SIS/RRHH/AST, asignados por persona en
// Aplicaciones empresariales). Se RE-DERIVA en cada renovación del token: si a
// alguien le quitan el rol en el portal, la app lo pierde en la siguiente hora.

import * as AuthSession from 'expo-auth-session';
import Constants, { ExecutionEnvironment } from 'expo-constants';
import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';

WebBrowser.maybeCompleteAuthSession();

export const ENTRA = {
  TENANT_ID: '14d51063-89e0-498d-8e2f-0d382b61baf8',   // Ona Hotels and Apartments
  CLIENT_ID: 'd1d07b76-0710-4c4d-811d-917eccd6cdd5',   // registro «Ona Pulse»
};

/* `offline_access` es obligatorio: sin él Entra no emite refresh token y la
   sesión moriría a la hora, obligando a re-loguear a mano cada mañana. */
const SCOPES = ['openid', 'profile', 'email', 'offline_access', 'User.Read'];
const KEY_SESION = 'pulse.sesion';
const KEY_PKCE = 'pulse.pkce';   // verificador + estado mientras se va a Microsoft
const MARGEN_MS = 120_000;   // renovar 2 min antes de que caduque de verdad

/* Dónde puede funcionar el login:
   · App instalada (dev build, TestFlight, APK) → sí, redirect `onapulse://auth`.
   · Expo Go → NO: su esquema no se puede personalizar y Entra rechaza `exp://`.
   · Navegador → sí, PERO solo servido desde el dominio cuyo redirect está dado
     de alta en Entra como «Aplicación de página única». En localhost no, porque
     esa dirección no está registrada (y registrarla no compensa). */
export const ORIGEN_WEB = 'https://pulse.onasystem.net';
const enWeb = Platform.OS === 'web';
const webRegistrado = enWeb && typeof window !== 'undefined' && window.location?.origin === ORIGEN_WEB;
export const esExpoGo = Constants.executionEnvironment === ExecutionEnvironment.StoreClient;
export const loginDisponible = !esExpoGo && (!enWeb || webRegistrado);

/* El llavero del sistema no existe en navegador (el módulo web de
   expo-secure-store está vacío), así que allí se usa el almacenamiento local.
   🔴 Es menos hermético que el llavero: cualquier script de la página podría
   leerlo. Aceptable porque la web se sirve desde nuestro dominio, sin scripts
   de terceros, y porque el servidor valida cada petición por su cuenta — pero
   es una razón más para que la app instalada sea el destino final. */
const almacen = {
  async leer(k: string): Promise<string | null> {
    if (enWeb) { try { return globalThis.localStorage?.getItem(k) ?? null; } catch { return null; } }
    return SecureStore.getItemAsync(k);
  },
  async guardar(k: string, v: string): Promise<void> {
    if (enWeb) { try { globalThis.localStorage?.setItem(k, v); } catch { /* modo privado */ } return; }
    await SecureStore.setItemAsync(k, v);
  },
  async borrar(k: string): Promise<void> {
    if (enWeb) { try { globalThis.localStorage?.removeItem(k); } catch { /* nada */ } return; }
    await SecureStore.deleteItemAsync(k);
  },
};

const discovery = (): AuthSession.DiscoveryDocument => ({
  authorizationEndpoint: `https://login.microsoftonline.com/${ENTRA.TENANT_ID}/oauth2/v2.0/authorize`,
  tokenEndpoint: `https://login.microsoftonline.com/${ENTRA.TENANT_ID}/oauth2/v2.0/token`,
});

export type RolEntra = 'dg' | 'ops' | 'rev' | 'fin' | 'sis' | 'rrhh' | 'ast';
const ROLES_VALIDOS: RolEntra[] = ['dg', 'ops', 'rev', 'fin', 'sis', 'rrhh', 'ast'];

export interface Sesion {
  nombre: string;
  email: string;
  roles: string[];          // claim crudo del token (DG, OPS, …)
  rol: RolEntra | null;     // el primero reconocido, en minúsculas — null = sin rol asignado
  expira: number;           // epoch ms del access token
  refresh: string | null;   // refresh token (renovación silenciosa)
  idToken: string;          // se manda al API como Bearer: es quien te identifica allí
}

/* base64url → string. Hermes no garantiza atob/Buffer, así que va a mano.
   Verificado con round-trip contra Buffer, incluidos acentos y padding. */
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function b64urlDecode(s: string): string {
  let t = s.replace(/-/g, '+').replace(/_/g, '/');
  while (t.length % 4) t += '=';
  let out = '';
  for (let i = 0; i < t.length; i += 4) {
    const n = (B64.indexOf(t[i]) << 18) | (B64.indexOf(t[i + 1]) << 12)
      | ((B64.indexOf(t[i + 2]) & 63) << 6) | (B64.indexOf(t[i + 3]) & 63);
    out += String.fromCharCode((n >> 16) & 255);
    if (t[i + 2] !== '=') out += String.fromCharCode((n >> 8) & 255);
    if (t[i + 3] !== '=') out += String.fromCharCode(n & 255);
  }
  try { return decodeURIComponent(out.split('').map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')); }
  catch { return out; }
}

function claimsDelIdToken(idToken: string): Record<string, unknown> {
  const trozo = String(idToken || '').split('.')[1];
  if (!trozo) return {};
  try { return JSON.parse(b64urlDecode(trozo)); } catch { return {}; }
}

export function rolDeClaims(roles: string[]): RolEntra | null {
  for (const r of roles) {
    const v = String(r).toLowerCase() as RolEntra;
    if (ROLES_VALIDOS.includes(v)) return v;
  }
  return null;
}

/* El redirect de esta build. En Expo Go sale `exp://…` (inservible para Entra,
   ver cabecera); en build nativa, `onapulse://auth`. */
export const redirectUri = () => AuthSession.makeRedirectUri({ scheme: 'onapulse', path: 'auth' });

/* Construye la sesión desde una respuesta de token. Exige id_token: sin él no
   hay identidad ni rol, y guardar una sesión vacía sería peor que no entrar. */
function sesionDesdeToken(tok: AuthSession.TokenResponse, refreshPrevio: string | null): Sesion {
  const idToken = tok.idToken;
  if (!idToken) throw new Error('Entra no devolvió el token de identidad (falta el permiso «openid»).');
  const c = claimsDelIdToken(idToken);
  const roles = Array.isArray(c.roles) ? (c.roles as string[]).map(String) : [];
  return {
    nombre: String(c.name ?? ''),
    email: String(c.preferred_username ?? c.email ?? ''),
    roles,
    rol: rolDeClaims(roles),
    expira: Date.now() + (tok.expiresIn ?? 3600) * 1000,
    refresh: tok.refreshToken ?? refreshPrevio,
    idToken,
  };
}

async function guarda(s: Sesion): Promise<Sesion> {
  await almacen.guardar(KEY_SESION, JSON.stringify(s));
  return s;
}

/* ── Web: navegación completa, NO ventana emergente ────────────────────────
   Safari en el móvil bloquea las ventanas emergentes, y en una app añadida a
   la pantalla de inicio es peor todavía: si se abriera, lo haría fuera de la
   app. El flujo correcto en navegador es irse a Microsoft y volver a /auth,
   guardando entretanto el verificador PKCE y el estado.
   No devuelve nunca: la navegación se lleva la página por delante. */
async function loginWebRedirigiendo(): Promise<never> {
  const disco = discovery();
  const req = new AuthSession.AuthRequest({
    clientId: ENTRA.CLIENT_ID,
    scopes: SCOPES,
    redirectUri: redirectUri(),
    responseType: AuthSession.ResponseType.Code,
    usePKCE: true,
    extraParams: { prompt: 'select_account' },
  });
  const url = await req.makeAuthUrlAsync(disco);
  await almacen.guardar(KEY_PKCE, JSON.stringify({ v: req.codeVerifier ?? '', e: req.state ?? '' }));
  window.location.assign(url);
  return new Promise<never>(() => { /* la página se va */ });
}

/* Al volver de Microsoft: /auth?code=…&state=…  Devuelve la sesión, o null si
   esta carga no es una vuelta del login (que es lo normal). */
export async function completarLoginWeb(): Promise<Sesion | null> {
  if (!enWeb || typeof window === 'undefined') return null;
  const p = new URLSearchParams(window.location.search);
  const code = p.get('code');
  const err = p.get('error_description') || p.get('error');
  const limpiaUrl = () => window.history?.replaceState?.({}, '', window.location.pathname);

  if (err) { limpiaUrl(); throw new Error(`Entra rechazó el acceso: ${err}`); }
  if (!code) return null;

  const crudo = await almacen.leer(KEY_PKCE);
  await almacen.borrar(KEY_PKCE);
  limpiaUrl();
  if (!crudo) throw new Error('Se perdió el hilo del inicio de sesión. Inténtalo otra vez.');
  const { v: verifier, e: estado } = JSON.parse(crudo) as { v: string; e: string };

  // El «estado» es la defensa contra que alguien te cuele un código ajeno.
  if (estado && p.get('state') && p.get('state') !== estado) {
    throw new Error('La respuesta de Microsoft no corresponde a este inicio de sesión.');
  }

  const tok = await AuthSession.exchangeCodeAsync({
    clientId: ENTRA.CLIENT_ID,
    code,
    redirectUri: redirectUri(),
    extraParams: { code_verifier: verifier },
  }, discovery());
  return guarda(sesionDesdeToken(tok, null));
}

export async function loginEntra(): Promise<Sesion> {
  if (enWeb && loginDisponible) return loginWebRedirigiendo();
  if (!loginDisponible) {
    throw new Error('Este entorno no admite el login corporativo (Expo Go no puede personalizar su esquema de app). Hace falta la build de Ona Pulse: dev build o TestFlight.');
  }
  const disco = discovery();
  const req = new AuthSession.AuthRequest({
    clientId: ENTRA.CLIENT_ID,
    scopes: SCOPES,
    redirectUri: redirectUri(),
    responseType: AuthSession.ResponseType.Code,
    usePKCE: true,
    extraParams: { prompt: 'select_account' },
  });
  const res = await req.promptAsync(disco);
  if (res.type !== 'success') {
    if (res.type === 'cancel' || res.type === 'dismiss') throw new Error('Inicio de sesión cancelado.');
    if (res.type === 'locked') throw new Error('Ya hay un inicio de sesión abierto. Ciérralo y vuelve a intentarlo.');
    const err = 'error' in res && res.error ? res.error : null;
    throw new Error(err ? `Entra rechazó el acceso: ${err.description || err.code || err.message}` : 'No se pudo completar el inicio de sesión.');
  }
  const tok = await AuthSession.exchangeCodeAsync({
    clientId: ENTRA.CLIENT_ID,
    code: res.params.code,
    redirectUri: redirectUri(),
    extraParams: { code_verifier: req.codeVerifier ?? '' },
  }, disco);
  return guarda(sesionDesdeToken(tok, null));
}

/* Renovación silenciosa. Devuelve la sesión nueva (con el rol RE-LEÍDO del
   token fresco) o null si el refresh ya no vale (contraseña cambiada, acceso
   revocado, cuenta deshabilitada) → toca volver a entrar. */
/* 🔴 UNA SOLA RENOVACIÓN A LA VEZ. Esto era un bug de los caros: al abrir la
   app, la portada lanza cinco peticiones juntas (brief, hoteles, alertas,
   premio, versión). Si el token tocaba renovarse, salían CINCO renovaciones en
   paralelo — y Entra invalida el token de refresco en cuanto se usa. Una ganaba
   y las otras cuatro recibían `invalid_grant`; como aquí se cerraba la sesión al
   fallar, la sesión buena se borraba y las llamadas siguientes se iban con un
   401. En la app se veía como «SIN ACCESO» en la cabecera con datos reales
   debajo, que es lo que vio Patricio.

   Con el vuelo único, las cinco esperan la MISMA renovación y todas reciben la
   sesión nueva. Y solo se cierra sesión si de verdad el refresh ya no vale. */
let renovando: Promise<Sesion | null> | null = null;

async function renueva(s: Sesion): Promise<Sesion | null> {
  if (!s.refresh) return null;
  if (renovando) return renovando;
  renovando = (async () => {
    try {
      const tok = await AuthSession.refreshAsync(
        { clientId: ENTRA.CLIENT_ID, refreshToken: s.refresh!, scopes: SCOPES },
        discovery(),
      );
      return await guarda(sesionDesdeToken(tok, s.refresh));
    } catch {
      /* Antes de rendirse: puede que otra renovación (de otra pestaña, o de un
         arranque anterior) ya haya dejado una sesión buena en el almacén. Antes
         se cerraba la sesión a ciegas y se perdía una válida. */
      try {
        const raw = await almacen.leer(KEY_SESION);
        if (raw) {
          const nueva = JSON.parse(raw) as Sesion;
          if (nueva?.expira && Date.now() < nueva.expira - MARGEN_MS
              && nueva.refresh !== s.refresh) return nueva;
        }
      } catch { /* seguimos al cierre */ }
      await cerrarSesion();
      return null;
    } finally {
      renovando = null;
    }
  })();
  return renovando;
}

/* Sesión utilizable: caducada se intenta renovar; si no se puede, null. */
export async function sesionGuardada(): Promise<Sesion | null> {
  try {
    const raw = await almacen.leer(KEY_SESION);
    if (!raw) return null;
    const s = JSON.parse(raw) as Sesion;
    if (!s || typeof s.expira !== 'number') return null;
    if (Date.now() < s.expira - MARGEN_MS) return s;
    return await renueva(s);
  } catch { return null; }
}

export async function cerrarSesion(): Promise<void> {
  try { await almacen.borrar(KEY_SESION); } catch { /* nada que borrar */ }
}

/* Puerta biométrica (Face ID / huella) al reabrir con sesión guardada.
   Sin hardware o sin biometría dada de alta → pasa (no dejar fuera a nadie). */
export async function puertaBiometrica(): Promise<boolean> {
  try {
    const [hw, enrolled] = await Promise.all([
      LocalAuthentication.hasHardwareAsync(),
      LocalAuthentication.isEnrolledAsync(),
    ]);
    if (!hw || !enrolled) return true;
    const r = await LocalAuthentication.authenticateAsync({
      promptMessage: 'Desbloquear Ona Pulse',
      cancelLabel: 'Cancelar',
    });
    return r.success;
  } catch { return true; }
}
